import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { API_ENDPOINTS } from "../../../../constants/config"
import axiosInstance from "../../../../utils/axiosInstance"
import { toast } from "react-hot-toast"

const debounce = (func, delay) => {
  let debounceTimer
  return function () {
    const args = arguments
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => func.apply(this, args), delay)
  }
}

export const getFullName = (person) => {
  if (!person) return "Unknown"
  return [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ") || "Unknown"
}

export const getInitials = (person) => {
  if (!person) return "??"
  const firstName = person.firstName ? person.firstName.charAt(0) : "?"
  const lastName = person.lastName ? person.lastName.charAt(0) : "?"
  return `${firstName}${lastName}`.toUpperCase()
}

export function useClassroomMapping() {
  const navigate = useNavigate()

  const [teachers, setTeachers] = useState([])
  const [students, setStudents] = useState([])
  const [classes, setClasses] = useState([])
  const [sections, setSections] = useState([])
  const [teacherMappings, setTeacherMappings] = useState([])
  const [studentMappings, setStudentMappings] = useState([])

  const [selectedTeacher, setSelectedTeacher] = useState("")
  const [currentTeacherClass, setCurrentTeacherClass] = useState("")
  const [currentTeacherSection, setCurrentTeacherSection] = useState("")
  const [teacherType, setTeacherType] = useState("subject_teacher")

  const [selectedStudent, setSelectedStudent] = useState("")
  const [currentStudentClass, setCurrentStudentClass] = useState("")
  const [currentStudentSection, setCurrentStudentSection] = useState("")

  const [selectedSubjects, setSelectedSubjects] = useState([])
  const [availableSubjects, setAvailableSubjects] = useState([])
  const [filteredSubjects, setFilteredSubjects] = useState([])

  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [viewMode, setViewMode] = useState("teacher")
  const [teacherAvailableSections, setTeacherAvailableSections] = useState([])
  const [studentAvailableSections, setStudentAvailableSections] = useState([])
  const [activeTab, setActiveTab] = useState("teachers")

  const [searchQuery, setSearchQuery] = useState("")
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [sortDirection, setSortDirection] = useState("asc")
  const [sortColumn, setSortColumn] = useState("name")

  const [teacherPage, setTeacherPage] = useState(1)
  const [studentPage, setStudentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [teacherTotal, setTeacherTotal] = useState(0)
  const [studentTotal, setStudentTotal] = useState(0)
  const [teacherTableTeachers, setTeacherTableTeachers] = useState([])
  const [studentTableStudents, setStudentTableStudents] = useState([])

  const validateTokenAndRedirect = () => {
    const token = localStorage.getItem("token")
    if (!token) {
      navigate("/login")
      return false
    }
    try {
      const base64Url = token.split(".")[1]
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
      const jsonPayload = decodeURIComponent(
        atob(base64).split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join(""),
      )
      const { exp } = JSON.parse(jsonPayload)
      if (exp * 1000 < Date.now()) {
        localStorage.removeItem("token")
        navigate("/login")
        return false
      }
      return true
    } catch {
      localStorage.removeItem("token")
      navigate("/login")
      return false
    }
  }

  const fetchTeachers = async () => {
    try {
      setIsLoading(true)
      if (!validateTokenAndRedirect()) return
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.usersTeachers, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "all" },
      })
      if (response.data.status !== "success") throw new Error(response.data.message || "Failed to fetch teachers")
      setTeachers(response.data.data.teachers || [])
    } catch {
      setError("Failed to fetch teachers")
      toast.error("Failed to fetch teachers. Please try again.")
      setTeachers([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTeacherPage = async () => {
    try {
      setIsLoading(true)
      if (!validateTokenAndRedirect()) return
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.usersTeachers, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          paginate: true,
          page: teacherPage,
          limit: itemsPerPage,
          search: searchQuery?.trim() ? searchQuery.trim() : undefined,
          status: "all",
        },
      })
      if (response.data.status !== "success") throw new Error(response.data.message || "Failed to fetch teachers")
      setTeacherTableTeachers(response.data.data.teachers || [])
      setTeacherTotal(Number(response.data.data.count || 0))
    } catch {
      setError("Failed to fetch teachers")
      setTeacherTableTeachers([])
      setTeacherTotal(0)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStudents = async () => {
    try {
      setIsLoading(true)
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.usersStudents, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "all" },
      })
      setStudents(response.data.data.students || [])
    } catch {
      setError("Failed to fetch students")
      setStudents([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStudentPage = async () => {
    try {
      setIsLoading(true)
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.usersStudents, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          paginate: true,
          page: studentPage,
          limit: itemsPerPage,
          search: searchQuery?.trim() ? searchQuery.trim() : undefined,
          status: "all",
        },
      })
      setStudentTableStudents(response.data.data.students || [])
      setStudentTotal(Number(response.data.data.count || 0))
    } catch {
      setError("Failed to fetch students")
      setStudentTableStudents([])
      setStudentTotal(0)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAllMappings = async () => {
    try {
      setIsLoading(true)
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.classroomMappings, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.data.data) {
        const studentMaps = (response.data.data.studentEnrollments || [])
          .filter((e) => e.student && e.class && e.section)
          .map((e) => ({ _id: e._id, user: e.student, class: e.class, section: e.section, type: null }))

        const teacherMaps = (response.data.data.teacherAssignments || []).flatMap((assignment) =>
          assignment.assignments
            .filter((ta) => ta.class && ta.section && ta.class._id && ta.section._id)
            .map((ta) => ({
              _id: `${assignment._id}-${ta.class._id}-${ta.section._id}`,
              user: assignment.teacher,
              class: ta.class,
              section: ta.section,
              type: ta.isClassTeacher ? "class_teacher" : "subject_teacher",
              subjects: [ta.subject],
            })),
        )

        setTeacherMappings(teacherMaps)
        setStudentMappings(studentMaps)
      }
    } catch {
      setError("Failed to fetch classroom mappings")
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTeacherMapping = async (teacherId) => {
    if (!teacherId) return
    try {
      const currentType = teacherType
      const currentClass = currentTeacherClass
      const currentSection = currentTeacherSection
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.classroomMappingsUser(teacherId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.data.data.assignment?.assignments?.length > 0) {
        const first = response.data.data.assignment.assignments[0]
        if (!currentClass) setCurrentTeacherClass(first.class._id)
        if (!currentSection) setCurrentTeacherSection(first.section._id)
        if (!currentType) setTeacherType(first.isClassTeacher ? "class_teacher" : "subject_teacher")
      }
    } catch (error) {
      if (error.response?.status !== 404) setError("Failed to fetch teacher mapping")
    }
  }

  const fetchStudentMapping = async (studentId) => {
    if (!studentId) return
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.classroomMappingsUser(studentId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.data.data.enrollment) {
        setCurrentStudentClass(response.data.data.enrollment.class._id)
        setCurrentStudentSection(response.data.data.enrollment.section._id)
      } else {
        setCurrentStudentClass("")
        setCurrentStudentSection("")
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setCurrentStudentClass("")
        setCurrentStudentSection("")
      } else {
        setError("Failed to fetch student mapping")
      }
    }
  }

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.classes, { headers: { Authorization: `Bearer ${token}` } })
      setClasses(response.data.data.classes)
    } catch {
      setError("Failed to fetch classes")
    }
  }

  const fetchSections = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.sections, { headers: { Authorization: `Bearer ${token}` } })
      if (response.data.status === "success") setSections(response.data.data.sections)
    } catch {
      setError("Failed to fetch sections")
    }
  }

  const fetchSectionsByClass = async (classId, userType) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.sectionsByClass(classId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (userType === "teacher") setTeacherAvailableSections(response.data.data.sections)
      else setStudentAvailableSections(response.data.data.sections)
    } catch (error) {
      if (error.response?.status === 404) {
        if (userType === "teacher") setTeacherAvailableSections([])
        else setStudentAvailableSections([])
      } else {
        setError("Failed to fetch sections for selected class")
      }
    }
  }

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axiosInstance.get(API_ENDPOINTS.subjects, { headers: { Authorization: `Bearer ${token}` } })
      setAvailableSubjects(response.data.data.subjects)
    } catch {
      setError("Failed to fetch subjects")
    }
  }

  const fetchTeacherSubjects = async (teacherId) => {
    try {
      const token = localStorage.getItem("token")
      const response = await fetch(API_ENDPOINTS.usersTeachers, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      })
      const data = await response.json()
      const teachersList = data.data.teachers
      const selected = teachersList.find((t) => t._id === teacherId)
      if (selected) {
        const teacherSubjectIds = selected.subjects.map((s) => s._id)
        setFilteredSubjects(availableSubjects.filter((s) => teacherSubjectIds.includes(s._id)))
      }
    } catch (error) {
      console.error("Error fetching teacher subjects:", error)
    }
  }

  const handleTeacherChange = (teacherId) => {
    if (teacherId && teacherId.includes(",")) {
      setError("Please select only one teacher at a time")
      toast.error("Please select only one teacher at a time")
      return
    }
    setSelectedTeacher(teacherId)
    if (teacherId && !teacherId.includes(",")) {
      fetchTeacherMapping(teacherId)
      fetchTeacherSubjects(teacherId)
    }
  }

  const handleStudentChange = (studentId) => {
    setSelectedStudent(studentId)
    if (studentId && !studentId.includes(",")) {
      fetchStudentMapping(studentId)
    } else if (studentId && studentId.includes(",")) {
      setCurrentStudentClass("")
      setCurrentStudentSection("")
    }
  }

  const saveTeacherMapping = async () => {
    if (!selectedTeacher || !currentTeacherClass || !currentTeacherSection) {
      setError("Please select teacher, class, and section")
      toast.error("Please select teacher, class, and section")
      return
    }
    if (selectedTeacher.includes(",")) {
      setError("Please select only one teacher at a time")
      toast.error("Please select only one teacher at a time")
      return
    }
    if (selectedSubjects.length === 0) {
      setError("Please select at least one subject")
      toast.error("Please select at least one subject")
      return
    }
    try {
      setIsLoading(true)
      setError("")
      const token = localStorage.getItem("token")
      const response = await axiosInstance.post(
        API_ENDPOINTS.classroomMappings,
        { userId: selectedTeacher, classId: currentTeacherClass, sectionId: currentTeacherSection, type: teacherType, subjects: selectedSubjects },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } },
      )
      if (response.status === 200) {
        setIsSuccess(true)
        await fetchAllMappings()
        const teacherObj = teachers.find((t) => t._id === selectedTeacher)
        const teacherName = teacherObj ? getFullName(teacherObj) : "Teacher"
        toast.success(`${teacherName} successfully assigned as ${teacherType === "class_teacher" ? "class teacher" : "subject teacher"}`)
        setSelectedTeacher("")
        setCurrentTeacherClass("")
        setCurrentTeacherSection("")
        setTeacherType("subject_teacher")
        setSelectedSubjects([])
        setFilteredSubjects(availableSubjects)
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to assign class and section"
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  const saveStudentMapping = async () => {
    try {
      setIsLoading(true)
      setError("")
      if (!selectedStudent || !currentStudentClass || !currentStudentSection) {
        setError("Please select student, class, and section")
        toast.error("Please select student, class, and section")
        setIsLoading(false)
        return
      }
      if (typeof selectedStudent === "string" && selectedStudent.includes(",")) {
        const token = localStorage.getItem("token")
        const studentIds = selectedStudent.split(",").filter((id) => id.trim())
        if (studentIds.length === 0) {
          setError("No valid student IDs found")
          toast.error("No valid student IDs found")
          setIsLoading(false)
          return
        }
        await axiosInstance.post(
          API_ENDPOINTS.classroomMappingsBulkAssign,
          { studentIds, classId: currentStudentClass, sectionId: currentStudentSection },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        )
        setIsSuccess(true)
        toast.success(`${studentIds.length} students successfully assigned to class`)
        setTimeout(() => setIsSuccess(false), 2000)
        fetchAllMappings()
        setSelectedStudent("")
        setCurrentStudentClass("")
        setCurrentStudentSection("")
        setIsLoading(false)
        return
      }
      const token = localStorage.getItem("token")
      const existingMappings = getStudentMappings(selectedStudent)
      if (existingMappings.length > 0) {
        const existing = existingMappings[0]
        if (existing.class?._id !== currentStudentClass || existing.section?._id !== currentStudentSection) {
          const ok = window.confirm("This student is already assigned to another class. Continuing will move them to the new class. Are you sure?")
          if (!ok) { setIsLoading(false); return }
        }
      }
      const cleanStudentId = selectedStudent.trim()
      if (!cleanStudentId || cleanStudentId.includes(",")) {
        setError("Invalid student selection. Please select only one student.")
        toast.error("Invalid student selection. Please select only one student.")
        setIsLoading(false)
        return
      }
      await axiosInstance.post(
        API_ENDPOINTS.classroomMappings,
        { userId: cleanStudentId, classId: currentStudentClass, sectionId: currentStudentSection, type: null },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      setIsSuccess(true)
      const studentObj = students.find((s) => s._id === cleanStudentId)
      const studentName = studentObj ? getFullName(studentObj) : "Student"
      toast.success(`${studentName} successfully assigned to class`)
      setTimeout(() => setIsSuccess(false), 2000)
      fetchAllMappings()
      setSelectedStudent("")
      setCurrentStudentClass("")
      setCurrentStudentSection("")
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Error saving student mapping"
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  const getTeacherMappings = (teacherId) =>
    teacherMappings.filter((m) => m.user && m.user._id === teacherId)

  const getStudentMappings = (studentId) =>
    studentMappings.filter((m) => m.user && String(m.user._id) === String(studentId))

  const getClassSectionMappings = (classId, sectionId, mappingType) => {
    const mappings = mappingType === "teacher" ? teacherMappings : studentMappings
    if (!Array.isArray(mappings)) return []
    return mappings.filter((m) => m?.class?._id === classId && m?.section?._id === sectionId)
  }

  const handleDeleteMapping = async (mappingId, userType, userName) => {
    const confirmed = window.confirm(`Are you sure you want to delete ${userName}'s assignment? This action cannot be undone.`)
    if (!confirmed) return
    try {
      setIsLoading(true)
      const token = localStorage.getItem("token")
      const response = await axiosInstance.delete(API_ENDPOINTS.classroomMapping(mappingId, userType), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.status === 200) {
        toast.success(`${userName}'s assignment deleted successfully`)
        fetchAllMappings()
      }
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to delete mapping"
      setError(msg)
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleTeacherClick = (teacher) => {
    const slug = getFullName(teacher).toLowerCase().replace(/\s+/g, "-")
    navigate(`/dashboard/teachers/${slug}`)
  }

  const handleStudentClick = (student) => {
    const slug = `${student.firstName} ${student.middleName || ""} ${student.lastName}`.trim().toLowerCase().replace(/\s+/g, "-")
    navigate(`/dashboard/students/${slug}`)
  }

  const handleSearchChange = debounce((e) => {
    const query = e.target.value
    setSearchQuery(query)
    if (query.length >= 2) {
      const suggestions =
        activeTab === "teachers"
          ? teachers.filter((t) => t.firstName.toLowerCase().includes(query.toLowerCase()) || t.lastName.toLowerCase().includes(query.toLowerCase())).slice(0, 5)
          : students.filter((s) => s.firstName.toLowerCase().includes(query.toLowerCase()) || s.lastName.toLowerCase().includes(query.toLowerCase()) || (s.admissionNumber && s.admissionNumber.toString().includes(query))).slice(0, 5)
      setSearchSuggestions(suggestions)
      setShowSuggestions(true)
    } else {
      setSearchSuggestions([])
      setShowSuggestions(false)
    }
  }, 300)

  const handleSuggestionClick = (person) => {
    setSearchQuery(`${person.firstName} ${person.lastName}`)
    setSearchSuggestions([])
    setShowSuggestions(false)
  }

  const handleSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc"
    setSortColumn(column)
    setSortDirection(newDirection)
  }

  const toggleSubject = (subjectId) => {
    setSelectedSubjects((prev) =>
      prev.includes(subjectId) ? prev.filter((id) => id !== subjectId) : [...prev, subjectId],
    )
  }

  const getSortedTeachers = (list) =>
    [...list].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
      return sortDirection === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA)
    })

  const getSortedStudents = (list) =>
    [...list].sort((a, b) => {
      if (sortColumn === "name") {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
        return sortDirection === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA)
      }
      if (sortColumn === "admissionNo") {
        const admA = a.admissionNumber || ""; const admB = b.admissionNumber || ""
        return sortDirection === "asc" ? admA.localeCompare(admB) : admB.localeCompare(admA)
      }
      if (sortColumn === "class") {
        const classA = a.classMapping?.class?.name || ""; const classB = b.classMapping?.class?.name || ""
        return sortDirection === "asc" ? classA.localeCompare(classB) : classB.localeCompare(classA)
      }
      return 0
    })

  const sortedTeachers = getSortedTeachers(teacherTableTeachers)
  const sortedStudents = getSortedStudents(studentTableStudents)

  useEffect(() => {
    fetchTeachers(); fetchStudents(); fetchClasses(); fetchSections(); fetchSubjects(); fetchAllMappings()
  }, [])

  useEffect(() => { setFilteredSubjects(availableSubjects) }, [availableSubjects])

  useEffect(() => {
    if (currentTeacherClass) fetchSectionsByClass(currentTeacherClass, "teacher")
    else { setTeacherAvailableSections([]); setCurrentTeacherSection("") }
  }, [currentTeacherClass])

  useEffect(() => {
    if (currentStudentClass) fetchSectionsByClass(currentStudentClass, "student")
    else { setStudentAvailableSections([]); setCurrentStudentSection("") }
  }, [currentStudentClass])

  useEffect(() => {
    setSections((prev) => {
      const next = [...prev]
      ;[...teacherAvailableSections, ...studentAvailableSections].forEach((s) => {
        if (!next.find((x) => x._id === s._id)) next.push(s)
      })
      return next
    })
  }, [teacherAvailableSections, studentAvailableSections])

  useEffect(() => { setTeacherPage(1); setStudentPage(1) }, [searchQuery, activeTab])

  useEffect(() => {
    if (activeTab === "teachers" && viewMode === "teacher") fetchTeacherPage()
  }, [activeTab, viewMode, teacherPage, itemsPerPage, searchQuery])

  useEffect(() => {
    if (activeTab === "students" && viewMode === "student") fetchStudentPage()
  }, [activeTab, viewMode, studentPage, itemsPerPage, searchQuery])

  useEffect(() => {
    validateTokenAndRedirect()
    const interval = setInterval(validateTokenAndRedirect, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  return {
    teachers, students, classes, sections, teacherMappings, studentMappings,
    selectedTeacher, setSelectedTeacher,
    currentTeacherClass, setCurrentTeacherClass,
    currentTeacherSection, setCurrentTeacherSection,
    teacherType, setTeacherType,
    selectedStudent,
    currentStudentClass, setCurrentStudentClass,
    currentStudentSection, setCurrentStudentSection,
    selectedSubjects, filteredSubjects,
    error, isLoading, isSuccess,
    viewMode, setViewMode,
    teacherAvailableSections, studentAvailableSections,
    activeTab, setActiveTab,
    searchQuery, searchSuggestions, showSuggestions,
    sortDirection, sortColumn,
    teacherPage, setTeacherPage,
    studentPage, setStudentPage,
    itemsPerPage, setItemsPerPage,
    teacherTotal, studentTotal,
    sortedTeachers, sortedStudents,
    handleTeacherChange, handleStudentChange,
    saveTeacherMapping, saveStudentMapping,
    getTeacherMappings, getStudentMappings, getClassSectionMappings,
    handleDeleteMapping,
    handleTeacherClick, handleStudentClick,
    handleSearchChange, handleSuggestionClick,
    handleSort,
    toggleSubject,
    setSortColumn, setSortDirection,
  }
}
